import React, { useEffect, useState } from "react";

/**
 * MessageCarousel
 * 
 * Shows last 3 messages for a port with up/down navigation.
 * Props:
 * - messages: array of message objects (newest first)
 * - portNumber: string for display
 */
export default function MessageCarousel({ messages, portNumber }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const newestId = messages?.[0]?.id ?? null;

  // A new SMS should show immediately, even if an older one was open.
  useEffect(() => {
    setCurrentIndex(0);
  }, [newestId]);

  if (!messages || messages.length === 0) {
    return (
      <div className="text-muted small" style={{ fontSize: "0.75rem" }}>
        No messages yet
      </div>
    );
  }

  const safeIndex = Math.min(currentIndex, messages.length - 1);
  const currentMessage = messages[safeIndex];
  const isNewest = safeIndex === 0;

  const goToPrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => Math.min(messages.length - 1, prev + 1));
  };

  const formatTimestamp = (ts) => {
    try {
      const date = new Date(ts);
      return date.toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return ts;
    }
  };

  return (
    <div
      className="border rounded p-2"
      style={{
        fontSize: "0.75rem",
        backgroundColor: isNewest ? "#f8f9fa" : "#fff3cd",
        minWidth: 280,
        maxWidth: 320,
      }}
    >
      {/* Single line header with all metadata and navigation */}
      <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: "0.7rem" }}>
        {/* Left side: Direction and From/To */}
        <div className="d-flex align-items-center gap-1" style={{ flex: 1, minWidth: 0 }}>
          <span>
            {currentMessage.is_outgoing ? (
              <span className="text-primary">→ Out</span>
            ) : (
              <span className="text-success">← In</span>
            )}
          </span>
          <span className="text-muted" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {currentMessage.is_outgoing ? currentMessage.receiver : currentMessage.sender}
          </span>
        </div>

        {/* Middle: Date */}
        <span className="text-muted mx-2" style={{ whiteSpace: "nowrap" }}>
          {formatTimestamp(currentMessage.timestamp)}
        </span>

        {/* Right side: Badge and navigation */}
        <div className="d-flex align-items-center gap-1">
          <span
            className={`badge ${
              isNewest ? "bg-success" : "bg-warning text-dark"
            }`}
            style={{ fontSize: "0.6rem" }}
          >
            {isNewest ? "LATEST" : `${safeIndex + 1}/${messages.length}`}
          </span>
          <button
            className="btn btn-sm btn-outline-secondary py-0 px-1"
            onClick={goToPrevious}
            disabled={safeIndex === 0}
            style={{ fontSize: "0.6rem", lineHeight: 1 }}
            title="Newer message"
          >
            ▲
          </button>
          <button
            className="btn btn-sm btn-outline-secondary py-0 px-1"
            onClick={goToNext}
            disabled={safeIndex === messages.length - 1}
            style={{ fontSize: "0.6rem", lineHeight: 1 }}
            title="Older message"
          >
            ▼
          </button>
        </div>
      </div>

      {/* Message content - only 2 lines */}
      <div
        className="small"
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.3",
        }}
      >
        {currentMessage.message}
      </div>
    </div>
  );
}